import React, { useEffect, useRef, useState } from 'react';
import { EditorContent } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { Button } from '@/components/Button';
import { ComposeToolbar } from '@/components/ComposeToolbar';
import { AddCircle } from '@/components/Icons/AddCircle';
import { ChevronLeft } from '@/components/Icons/ChevronLeft';
import { Close } from '@/components/Icons/Close';
import { Paperclip } from '@/components/Icons/Paperclip';
import { PlusSign } from '@/components/Icons/PlusSign';
import { Send } from '@/components/Icons/Send';
import { LinkModal } from '@/components/LinkModal';
import { PendingAttachmentChip } from '@/components/PendingAttachmentChip';
import { Snackbar } from '@/components/Snackbar';
import { Modal } from '@/components/Modal';
import { useCloseOnBreakpointChange } from '@/hooks/useCloseOnBreakpointChange';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { useVisualViewport } from '@/hooks/useVisualViewport';
import { MailReplyQuote } from '@/pages/Mail/components/MailReplyQuote';
import { RecipientPickerView } from '@/pages/Mail/components/RecipientPickerView';
import { useComposeForm } from '@/pages/Mail/hooks/useComposeForm';
import {
  BorderRadius,
  ButtonVariant,
  MailComposeMode,
  MailFolderType,
  ModalVariant,
  Size,
  SnackbarVariant,
} from '@/types/components';
import type { MailMessage, Recipient } from '@/types/mail';
import { getRecipientKey } from '@/utils/mail';
import { warmUpCapturePermission } from '@/utils/permissions';
import { capitalizeSentenceStart } from '@/utils/text';

const MOBILE_RECIPIENT_LIMIT = 2;
const DESKTOP_RECIPIENT_LIMIT = 64;

const overflowChipClassName =
  'flex items-center rounded-[40px] bg-secondary-pressed px-3 py-1 text-body-13 text-foreground';

interface Props {
  mode: MailComposeMode;
  folderType: MailFolderType;
  sourceMail?: MailMessage | null;
  clickOutsideRef: React.RefObject<(() => boolean) | null>;
  onClose: () => void;
  onSent: () => void;
}

export const ComposeForm: React.FC<Props> = ({
  mode,
  folderType,
  sourceMail,
  clickOutsideRef,
  onClose,
  onSent,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const visualViewport = useVisualViewport();
  const {
    editor,
    subject,
    setSubject,
    recipients,
    setRecipients,
    attachments,
    addFiles,
    removeAttachment,
    quote,
    quotedMail,
    isEditorFocused,
    isCharLimitReached,
    isSending,
    isSendErrorOpen,
    canSend,
    errorRecipients,
    errorSubject,
    errorBody,
    handleSend,
    dismissSendError,
  } = useComposeForm({ mode, folderType, sourceMail, onSent });

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isLimitSnackbarOpen, setIsLimitSnackbarOpen] = useState(false);
  const [isRecipientSheetOpen, setIsRecipientSheetOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const pickerBackRef = useRef<(() => void) | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { sheetRef, swipeProps } = useSwipeToClose(() =>
    setIsRecipientSheetOpen(false),
  );

  const recipientLimit = isMobile
    ? MOBILE_RECIPIENT_LIMIT
    : DESKTOP_RECIPIENT_LIMIT;
  const visibleRecipients = recipients.slice(0, recipientLimit);
  const overflowCount = recipients.length - visibleRecipients.length;
  const keyboardAwareStyle =
    isMobile && visualViewport
      ? {
          height: visualViewport.height,
          transform: `translateY(${visualViewport.offsetTop}px)`,
        }
      : undefined;

  const openFileDialog = async () => {
    await warmUpCapturePermission();
    fileInputRef.current?.click();
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      addFiles(Array.from(event.target.files));
    }

    event.target.value = '';
  };

  const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const caret = input.selectionStart ?? input.value.length;
    const nextSubject =
      capitalizeSentenceStart(input.value.slice(0, caret)) +
      input.value.slice(caret);

    if (nextSubject !== input.value) {
      input.value = nextSubject;
      input.setSelectionRange(caret, caret);
    }

    setSubject(nextSubject);
  };

  const removeRecipient = (recipient: Recipient) => {
    const next = recipients.filter(
      (item) => getRecipientKey(item) !== getRecipientKey(recipient),
    );
    setRecipients(next);

    if (next.length === 0) {
      setIsRecipientSheetOpen(false);
    }
  };

  const preventEditorBlur = (event: React.MouseEvent) => {
    if (
      event.target instanceof HTMLElement &&
      !event.target.closest('.tiptap')
    ) {
      event.preventDefault();
    }
  };

  const focusEditor = () => {
    if (editor?.isFocused) {
      return;
    }

    editor?.commands.focus();
  };

  useCloseOnBreakpointChange(isRecipientSheetOpen, () =>
    setIsRecipientSheetOpen(false),
  );

  useEffect(() => {
    if (isCharLimitReached) {
      setIsLimitSnackbarOpen(true);
    }
  }, [isCharLimitReached]);

  useEffect(() => {
    clickOutsideRef.current = () => {
      if (isPickerOpen) {
        pickerBackRef.current?.();
        return true;
      }

      return false;
    };
  }, [isPickerOpen, clickOutsideRef]);

  return (
    <div style={keyboardAwareStyle} className="relative flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between px-4 pt-[calc(24px+env(safe-area-inset-top))] pb-3 md:hidden">
        <button
          type="button"
          aria-label={t('mail.composeModal.back')}
          onClick={onClose}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
        >
          <ChevronLeft className="size-6" />
        </button>
        <button
          type="button"
          aria-label={t('mail.composeModal.attach')}
          onClick={openFileDialog}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
        >
          <Paperclip className="size-5.5" />
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={handleFilesSelected}
      />

      <div className="hidden shrink-0 items-center gap-3 px-5 pt-5 pb-4 md:flex">
        <h2 className="flex-1 text-headline-20 text-foreground">
          {t('mail.composeModal.title')}
        </h2>
        <button
          type="button"
          aria-label={t('global.close')}
          onClick={onClose}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
        >
          <Close />
        </button>
      </div>

      <div className="relative min-h-0 flex-1">
        <AutoHideScrollArea
          hasTopShadow
          hasBottomShadow
          className="size-full"
          viewportClassName="[&>div]:min-h-full"
        >
          <div className="flex min-h-full flex-col">
            <div className="flex shrink-0 flex-col border-b border-divider px-5">
              <button
                type="button"
                aria-label={t('mail.composeModal.addRecipients')}
                onClick={() => setIsPickerOpen(true)}
                className="flex h-14 w-full cursor-pointer items-center gap-2 text-left"
              >
                <span className="text-subhead-14 text-foreground max-md:text-headline-20">
                  {t('mail.composeModal.to')}
                </span>
                <span className="hidden items-center gap-1 rounded-xl bg-info-surface py-0.5 pr-2 pl-1 md:flex">
                  <AddCircle className="size-4 text-primary" />
                  <span className="text-caption-12 text-primary">
                    {t('mail.composeModal.addRecipients')}
                  </span>
                </span>
                <span className="ml-auto flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-white md:hidden">
                  <PlusSign className="size-3.5" strokeWidth="2.5" />
                </span>
              </button>
              {recipients.length > 0 && (
                <div className="flex flex-wrap gap-2 pb-4">
                  {visibleRecipients.map((recipient) => (
                    <span
                      key={getRecipientKey(recipient)}
                      className="flex items-center gap-2 rounded-[40px] bg-info-surface py-1 pr-2 pl-3 text-body-13 text-foreground"
                    >
                      {recipient.name} {recipient.description}
                      <button
                        type="button"
                        aria-label={recipient.name}
                        onClick={() => removeRecipient(recipient)}
                        className="flex size-4 shrink-0 cursor-pointer items-center justify-center text-subtle-foreground"
                      >
                        <Close className="size-3.5" strokeWidth="1.6" />
                      </button>
                    </span>
                  ))}
                  {overflowCount > 0 && isMobile && (
                    <button
                      type="button"
                      onClick={() => setIsRecipientSheetOpen(true)}
                      className={`cursor-pointer ${overflowChipClassName}`}
                    >
                      {t('mail.composeModal.recipientChip', {
                        count: overflowCount,
                      })}
                    </button>
                  )}
                  {overflowCount > 0 && !isMobile && (
                    <span className={overflowChipClassName}>
                      {t('mail.composeModal.recipientChip', {
                        count: overflowCount,
                      })}
                    </span>
                  )}
                </div>
              )}
              {errorRecipients && (
                <p className="pb-2 text-body-13 text-destructive">
                  {t('mail.composeModal.errors.recipients')}
                </p>
              )}
            </div>

            <div className="flex shrink-0 flex-col border-b border-divider px-5">
              <div className="flex h-14 items-center gap-2">
                <span className="shrink-0 text-subhead-14 text-foreground max-md:text-headline-20">
                  {t('mail.composeModal.subject')}
                </span>
                <input
                  autoCapitalize="sentences"
                  value={subject}
                  placeholder={t('mail.composeModal.subjectPlaceholder')}
                  onChange={handleSubjectChange}
                  className="min-w-0 flex-1 bg-transparent text-body-14 text-foreground outline-none placeholder:text-subtle-foreground"
                />
              </div>
              {errorSubject && (
                <p className="pb-2 text-body-13 text-destructive">
                  {t('mail.composeModal.errors.subject')}
                </p>
              )}
            </div>

            <div className="hidden shrink-0 px-5 pt-5 pb-1 md:block">
              <ComposeToolbar
                editor={editor}
                onLinkClick={() => setIsLinkModalOpen(true)}
              />
            </div>

            <div
              onMouseDown={preventEditorBlur}
              onClick={focusEditor}
              className="flex-1 cursor-text px-5 pt-5 pb-4 md:pt-2"
            >
              <EditorContent
                editor={editor}
                className="compose-editor rich-text text-body-14 text-foreground"
              />
              {errorBody && (
                <p className="pt-1 text-body-13 text-destructive">
                  {t('mail.composeModal.errors.body')}
                </p>
              )}
              {attachments.length > 0 && (
                <div
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                  className="mt-8 flex flex-col items-start gap-3 md:flex-row md:flex-wrap"
                >
                  {attachments.map((attachment) => (
                    <PendingAttachmentChip
                      key={attachment.key}
                      attachment={attachment}
                      onRemove={removeAttachment}
                    />
                  ))}
                </div>
              )}
              {quotedMail && (
                <div className="mt-8">
                  <MailReplyQuote mail={quotedMail} html={quote} />
                </div>
              )}
            </div>
          </div>
        </AutoHideScrollArea>
      </div>

      <div className="hidden shrink-0 items-center gap-3 bg-background px-5 pt-5 pb-6 md:flex">
        {isCharLimitReached && (
          <p className="flex h-10 min-w-0 items-center truncate rounded-2xl bg-red-soft px-3 text-subhead-14 text-destructive">
            {t('mail.composeModal.errors.bodyTooLong')}
          </p>
        )}
        <Button
          className="ml-auto shrink-0 text-subhead-14 font-semibold"
          variant={ButtonVariant.Secondary}
          onClick={openFileDialog}
        >
          <Paperclip className="size-5" />
          {t('mail.composeModal.attach')}
        </Button>
        <Button
          className="shrink-0 text-subhead-14 font-semibold"
          variant={ButtonVariant.Primary}
          disabled={!canSend}
          isLoading={isSending}
          onClick={handleSend}
        >
          <Send className="size-4.5" />
          {t('mail.composeModal.send')}
          {recipients.length > 0 && (
            <span className="flex h-6 min-w-6 items-center justify-center rounded-xl bg-background px-2 text-body-14 text-foreground">
              {recipients.length}
            </span>
          )}
        </Button>
      </div>

      <div className="flex shrink-0 items-center gap-3 px-4 pt-2 pb-[calc(12px+env(safe-area-inset-bottom))] md:hidden">
        {isEditorFocused && (
          <div onMouseDown={(event) => event.preventDefault()}>
            <ComposeToolbar
              editor={editor}
              onLinkClick={() => setIsLinkModalOpen(true)}
            />
          </div>
        )}
        <Button
          className="ml-auto shrink-0"
          variant={ButtonVariant.Primary}
          size={Size.Large}
          radius={BorderRadius.Pill}
          disabled={!canSend}
          isLoading={isSending}
          onClick={handleSend}
        >
          <Send className="size-4.5" />
          {t('mail.composeModal.send')}
          {recipients.length > 0 && (
            <span className="flex h-6 min-w-6 items-center justify-center rounded-xl bg-background px-2 text-body-14 text-foreground">
              {recipients.length}
            </span>
          )}
        </Button>
      </div>

      {isMobile && (
        <Snackbar
          message={t('mail.composeModal.errors.bodyTooLong')}
          isOpen={isLimitSnackbarOpen}
          variant={SnackbarVariant.Error}
          onDismiss={() => setIsLimitSnackbarOpen(false)}
        />
      )}

      <Snackbar
        message={t('global.errorOccurred')}
        isOpen={isSendErrorOpen}
        variant={SnackbarVariant.Error}
        onDismiss={dismissSendError}
      />

      <LinkModal
        editor={editor}
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
      />

      <Modal
        isOpen={isRecipientSheetOpen}
        label={t('mail.composeModal.recipientListTitle')}
        variant={ModalVariant.BottomSheet}
        contentRef={sheetRef}
        onClose={() => setIsRecipientSheetOpen(false)}
      >
        <div className="flex min-h-0 flex-col">
          <div
            {...swipeProps}
            className="shrink-0 px-4 pt-2 pb-3 max-md:cursor-grab max-md:touch-none max-md:active:cursor-grabbing"
          >
            <div className="mx-auto mb-3 h-[3px] w-10 rounded-full bg-drag-handle md:hidden" />
            <h2 className="text-title-36 text-foreground">
              {t('mail.composeModal.recipientListTitle')}
            </h2>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[calc(16px+env(safe-area-inset-bottom))]">
            {recipients.map((recipient) => (
              <div
                key={getRecipientKey(recipient)}
                className="flex items-center gap-3 border-b border-divider py-3 last:border-b-0"
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <p className="truncate text-headline-20 text-foreground">
                    {recipient.name}
                  </p>
                  <p className="truncate text-body-13 text-subtle-foreground">
                    {recipient.description}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={recipient.name}
                  onClick={() => removeRecipient(recipient)}
                  className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-subtle-foreground"
                >
                  <Close className="size-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {isPickerOpen && (
        <RecipientPickerView
          recipients={recipients}
          onClose={onClose}
          onRegisterBack={(requestBack) => {
            pickerBackRef.current = requestBack;
          }}
          onSave={(selected) => {
            setRecipients(selected);
            setIsPickerOpen(false);
          }}
        />
      )}
    </div>
  );
};
